/**
 * Package Selection Step - Step 3 of Onboarding
 * Lets new users choose a package and billing cycle before checkout.
 */

import { BillingCycleToggle, formatMoney } from '@/renderer/components/billing';
import {
  getAnnualDiscountPercentage,
  getPackageMonthlyEquivalent,
  getPackageTotalPriceForCycle,
} from '@/renderer/services/billingPricing';
import { BillingCycle } from '@/renderer/types/billing';
import React, { useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import {
  selectIsLoadingPackages,
  selectSubscriptionPackages,
} from '../../store/selectors';
import { getSubscriptionPackages } from '../../store/subscriptionPackagesSlice';
import {
  SubscriptionPackage,
  SubscriptionPackageInfo,
} from '../../types/subscription';
import { Button, Modal } from '../common';
import { OnboardingPackageCard } from './OnboardingPackageCard';

interface PackageSelectionStepProps {
  initialBillingCycle?: BillingCycle;
  initialPackage?: SubscriptionPackage;
  onBack: () => void;
  onNext: (packageId: SubscriptionPackage, billingCycle: BillingCycle) => void;
  isSubmitting?: boolean;
}

export const PackageSelectionStep: React.FC<PackageSelectionStepProps> = ({
  initialBillingCycle = 'annual',
  initialPackage,
  onBack,
  onNext,
  isSubmitting = false,
}) => {
  const dispatch = useAppDispatch();
  const packages = useAppSelector(selectSubscriptionPackages);
  const isLoading = useAppSelector(selectIsLoadingPackages);

  const [selectedPackage, setSelectedPackage] =
    useState<SubscriptionPackage | null>(
      initialPackage || SubscriptionPackage.PROFESSIONAL,
    );
  const [selectedBillingCycle, setSelectedBillingCycle] =
    useState<BillingCycle>(initialBillingCycle);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);

  useEffect(() => {
    if (packages.length === 0) {
      dispatch(getSubscriptionPackages());
    }
  }, [dispatch, packages.length]);

  useEffect(() => {
    if (initialPackage) {
      setSelectedPackage(initialPackage);
    }
  }, [initialPackage]);

  useEffect(() => {
    setSelectedBillingCycle(initialBillingCycle);
  }, [initialBillingCycle]);

  const comparisonFeatures = useMemo(
    () =>
      Array.from(new Set(packages.flatMap((pkg) => pkg.features))).sort(
        (left, right) => left.localeCompare(right),
      ),
    [packages],
  );

  const renderPlanPrice = (pkg: SubscriptionPackageInfo) => {
    const monthlyEquivalent = getPackageMonthlyEquivalent(
      pkg,
      selectedBillingCycle,
    );

    return (
      <>
        {selectedBillingCycle === 'annual' && (
          <p className="text-xs font-medium text-gray-400 line-through">
            {formatMoney(pkg.price)}/month
          </p>
        )}
        <div className="flex items-end justify-center gap-1">
          <span className="text-xl font-bold text-gray-900">
            {formatMoney(monthlyEquivalent)}
          </span>
          <span className="pb-0.5 text-xs text-gray-500">/month</span>
        </div>
        <p className="mt-1 text-[11px] text-gray-500">
          {selectedBillingCycle === 'annual'
            ? `${formatMoney(
                getPackageTotalPriceForCycle(pkg, selectedBillingCycle),
              )}/year`
            : 'Billed monthly'}
        </p>
      </>
    );
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (selectedPackage) {
      onNext(selectedPackage, selectedBillingCycle);
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-lg items-center justify-center py-12">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-pos-primary" />
          <p className="text-gray-600">Loading subscription packages...</p>
        </div>
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div className="mx-auto w-full max-w-lg">
        <div className="py-12 text-center">
          <p className="mb-4 text-gray-600">
            No subscription packages available.
          </p>
          <Button onClick={onBack} variant="outline">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl">
      <div className="mb-6 text-center">
        <h2 className="mb-2 text-3xl font-bold text-gray-900 font-sans">
          Choose Your Billing Plan
        </h2>
        <p className="mx-auto max-w-2xl text-sm leading-6 text-gray-500">
          Every plan starts with a 14-day free trial. Pick the package that fits
          your outlets, team size, and reporting needs, then add your payment
          method securely in Stripe.
        </p>
      </div>

      <div className="mb-6 flex flex-col items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 px-4 py-4 text-center">
        <div className="flex flex-wrap items-center justify-center gap-2">
          <span className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700">
            14-day free trial
          </span>
          {selectedBillingCycle === 'annual' && (
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700">
              Save up to 17%
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600">
          Professional is the best fit for most growing teams because it scales
          across outlets while keeping advanced analytics and AI menu tools.
        </p>
        <BillingCycleToggle
          value={selectedBillingCycle}
          onChange={setSelectedBillingCycle}
        />
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-6 flex flex-col gap-4">
          {packages.map((pkg) => (
            <OnboardingPackageCard
              key={pkg.id}
              billingCycle={selectedBillingCycle}
              isRecommended={pkg.id === SubscriptionPackage.PROFESSIONAL}
              isSelected={selectedPackage === pkg.id}
              onSelect={() => setSelectedPackage(pkg.id)}
              packageInfo={pkg}
            />
          ))}
        </div>

        <div className="mb-6 flex justify-center">
          <button
            type="button"
            onClick={() => setIsCompareModalOpen(true)}
            className="group flex items-center gap-1 text-sm font-semibold text-pos-primary transition-colors hover:text-pos-dark"
          >
            <span>Compare full package benefits</span>
            <svg
              className="h-4 w-4 transform transition-transform group-hover:translate-x-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            onClick={onBack}
            variant="outline"
            className="flex-1"
          >
            Back
          </Button>
          <Button
            type="submit"
            className="flex-[2]"
            disabled={!selectedPackage}
            isLoading={isSubmitting}
          >
            Continue to Billing Setup
          </Button>
        </div>
      </form>

      <Modal
        isOpen={isCompareModalOpen}
        onClose={() => setIsCompareModalOpen(false)}
        title="Compare All SmartPOS Cloud Features"
        size="xl"
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr>
                <th className="sticky top-0 min-w-[220px] border-b-2 border-gray-100 bg-gray-50/80 p-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
                    Package Details
                  </span>
                </th>
                {packages.map((pkg) => (
                  <th
                    key={pkg.id}
                    className="min-w-[170px] border-b-2 border-gray-100 bg-gray-50/80 p-4 text-center"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-lg font-bold text-gray-900">
                        {pkg.name}
                      </span>
                      {renderPlanPrice(pkg)}
                      {selectedBillingCycle === 'annual' && (
                        <span className="rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
                          Save {getAnnualDiscountPercentage(pkg)}%
                        </span>
                      )}
                      <Button
                        size="sm"
                        variant={
                          selectedPackage === pkg.id ? 'primary' : 'outline'
                        }
                        onClick={() => {
                          setSelectedPackage(pkg.id);
                          setIsCompareModalOpen(false);
                        }}
                        className="w-full text-xs"
                      >
                        {selectedPackage === pkg.id
                          ? 'Selected'
                          : 'Select Plan'}
                      </Button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {comparisonFeatures.map((feature, index) => (
                <tr
                  key={feature}
                  className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}
                >
                  <td className="p-4 font-medium text-gray-900">{feature}</td>
                  {packages.map((pkg) => (
                    <td
                      key={`${pkg.id}-${feature}`}
                      className="p-4 text-center"
                    >
                      {pkg.features.includes(feature) ? (
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  );
};
